/**
 * Admin · Product feedback (GET /admin/feedback, PATCH /admin/feedback/{id}; migration 0081).
 * Every note a signed-in person wrote from the header button, newest first, with the route it
 * was written on. Triage is two verbs — Reviewed and Closed — plus a private note; nothing here
 * is visible to the person who wrote the feedback.
 */
import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FEEDBACK_CATEGORY_LABELS, FEEDBACK_LIMITS, type FeedbackCategory, type FeedbackEntry, type FeedbackStatus } from "@talenthop/shared";
import { ApiError, api } from "../../lib/api.js";

interface FeedbackListResponse {
  entries: FeedbackEntry[];
  nextBefore: string | null;
  nextBeforeId: string | null;
  counts: Record<FeedbackStatus, number>;
}

/** One page boundary: the (created_at, id) of the last row shown, so a burst of same-millisecond notes pages cleanly. */
interface Cursor {
  before: string;
  beforeId: string | null;
}

type Filter = FeedbackStatus | "all";
const FILTERS: { key: Filter; label: string }[] = [
  { key: "new", label: "New" },
  { key: "reviewed", label: "Reviewed" },
  { key: "closed", label: "Closed" },
  { key: "all", label: "All" },
];

const categoryLabel = (c: string): string => FEEDBACK_CATEGORY_LABELS[c as FeedbackCategory] ?? c;

export function Feedback(): ReactNode {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("new");
  const [cursors, setCursors] = useState<Cursor[]>([]);
  const cursor = cursors.length > 0 ? cursors[cursors.length - 1]! : null;
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-feedback", filter, cursor?.before ?? null, cursor?.beforeId ?? null],
    queryFn: () =>
      api.get<FeedbackListResponse>(
        `/admin/feedback?limit=50${filter !== "all" ? `&status=${filter}` : ""}` +
          (cursor ? `&before=${encodeURIComponent(cursor.before)}${cursor.beforeId ? `&beforeId=${encodeURIComponent(cursor.beforeId)}` : ""}` : ""),
      ),
    retry: (count, err) => !(err instanceof ApiError && (err.status === 403 || err.status === 503)) && count < 2,
  });
  const errorText =
    error instanceof ApiError && error.status === 403
      ? "Product feedback is read by the platform Owner; this role cannot see it."
      : error instanceof ApiError && error.status === 503
        ? ((error.body as { message?: string } | undefined)?.message ?? "Feedback is not available yet.")
        : "Couldn't load feedback. Please retry in a moment.";
  const entries = data?.entries ?? [];
  const counts = data?.counts ?? { new: 0, reviewed: 0, closed: 0 };

  const review = useMutation({
    mutationFn: ({ id, status, adminNote }: { id: string; status?: FeedbackStatus; adminNote?: string | null }) =>
      api.patch<{ entry: FeedbackEntry }>(`/admin/feedback/${encodeURIComponent(id)}`, {
        ...(status ? { status } : {}),
        ...(adminNote !== undefined ? { adminNote } : {}),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin-feedback"] }),
  });

  const changeFilter = (f: Filter): void => {
    setFilter(f);
    setCursors([]);
    setExpandedId(null);
  };

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Product feedback</h1>
          <div className="sub">
            What people told us from the Feedback button, with the page they were on · nothing here is
            shown back to them
          </div>
        </div>
        <span className="pill n">Platform</span>
      </div>

      <div className="rq-chips" style={{ marginBottom: 14 }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`rq-chip${filter === f.key ? " on" : ""}`}
            onClick={() => changeFilter(f.key)}
          >
            {f.label}
            {f.key !== "all" && <span className="meta"> {counts[f.key]}</span>}
          </button>
        ))}
      </div>

      {isLoading && <div className="card pad mut sm">Loading feedback…</div>}
      {isError && (
        <div className="banner crit">
          <span>{errorText}</span>
        </div>
      )}

      {!isLoading && !isError && entries.length === 0 && (
        <div className="card pad">
          <p className="mut sm" style={{ margin: 0 }}>
            {filter === "new" ? "Nothing new — every note has been read." : "Nothing here yet."}
          </p>
        </div>
      )}

      {!isLoading && !isError && entries.length > 0 && (
        <div className="card">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Page</th>
                  <th>Kind</th>
                  <th>Note</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const expanded = expandedId === e.id;
                  const draft = noteDraft[e.id] ?? e.adminNote ?? "";
                  return (
                    <Fragment key={e.id}>
                      <tr onClick={() => setExpandedId(expanded ? null : e.id)} style={{ cursor: "pointer" }}>
                        <td className="mut sm tnum" style={{ whiteSpace: "nowrap" }}>
                          {new Date(e.createdAt).toLocaleString()}
                        </td>
                        <td className="sm">
                          <div style={{ fontWeight: 600 }}>{e.orgName ?? (e.persona === "admin" ? "TalentHop team" : "—")}</div>
                          <div className="mut">{e.userEmail ?? e.persona}</div>
                        </td>
                        <td className="mut sm" style={{ fontFamily: "var(--mono)", whiteSpace: "nowrap" }}>{e.pagePath || "—"}</td>
                        <td className="sm">
                          {categoryLabel(e.category)}
                          {e.rating !== null && <div className="mut">{"★".repeat(e.rating)}{"☆".repeat(5 - e.rating)}</div>}
                        </td>
                        <td className="sm" style={{ maxWidth: 460 }}>
                          <span style={expanded ? undefined : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {e.message}
                          </span>
                        </td>
                        <td>
                          <span className={`pill ${e.status === "new" ? "n" : e.status === "reviewed" ? "v" : "t"}`}>{e.status}</span>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={6} style={{ background: "var(--surface-2)" }}>
                            <div style={{ display: "grid", gap: 10, padding: "6px 2px 8px" }}>
                              <div style={{ whiteSpace: "pre-wrap", fontSize: 13.5 }}>{e.message}</div>
                              <label className="rq-lab" htmlFor={`fb-note-${e.id}`}>
                                Our note <span className="h">— private to the team</span>
                              </label>
                              <textarea
                                id={`fb-note-${e.id}`}
                                className="rq-in"
                                rows={2}
                                maxLength={FEEDBACK_LIMITS.maxAdminNote}
                                value={draft}
                                onChange={(ev) => setNoteDraft((p) => ({ ...p, [e.id]: ev.target.value }))}
                                placeholder="What we decided, or who picked it up"
                              />
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                {e.status !== "reviewed" && (
                                  <button type="button" className="rq-btn primary sm" disabled={review.isPending} onClick={() => review.mutate({ id: e.id, status: "reviewed", adminNote: draft.trim() || null })}>
                                    Mark reviewed
                                  </button>
                                )}
                                {e.status !== "closed" && (
                                  <button type="button" className="rq-btn ghost sm" disabled={review.isPending} onClick={() => review.mutate({ id: e.id, status: "closed", adminNote: draft.trim() || null })}>
                                    Close
                                  </button>
                                )}
                                {e.status !== "new" && (
                                  <button type="button" className="rq-btn ghost sm" disabled={review.isPending} onClick={() => review.mutate({ id: e.id, status: "new" })}>
                                    Reopen
                                  </button>
                                )}
                                <button type="button" className="rq-btn ghost sm" disabled={review.isPending || draft === (e.adminNote ?? "")} onClick={() => review.mutate({ id: e.id, adminNote: draft.trim() || null })}>
                                  Save note
                                </button>
                                {e.reviewedAt && (
                                  <span className="mut sm">
                                    last touched {new Date(e.reviewedAt).toLocaleString()}
                                  </span>
                                )}
                                {review.isError && <span className="sm" style={{ color: "var(--crit)" }}>That didn't save — retry.</span>}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 8, padding: 12, justifyContent: "flex-end" }}>
            <button type="button" className="rq-btn ghost sm" disabled={cursors.length === 0} onClick={() => setCursors((c) => c.slice(0, -1))}>
              Newer
            </button>
            <button
              type="button"
              className="rq-btn ghost sm"
              disabled={!data?.nextBefore}
              onClick={() => data?.nextBefore && setCursors((c) => [...c, { before: data.nextBefore!, beforeId: data.nextBeforeId ?? null }])}
            >
              Older
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
